"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CircleCheck, ChartLine, Users, PenTool, Sparkles, ArrowRight } from "lucide-react";
const tabsData = [{
  id: "volunteering",
  label: "Volunteering",
  icon: Users,
  title: "Hands-On Community Impact",
  description: "Join our frontline efforts and make a tangible difference in the lives of those we serve. Whether it's local outreach or international aid, your time is the most valuable gift you can offer.",
  features: ["Flexible scheduling for local community projects", "Specialized skill-based volunteering opportunities", "Comprehensive orientation and safety training", "Direct interaction with beneficiary communities"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687075/img/helping_hands_volunteer_action.png",
  badge: "Direct Action"
}, {
  id: "corporate",
  label: "Corporate",
  icon: ChartLine,
  title: "Strategic Partnership & CSR",
  description: "Align your business goals with social impact. We work with corporate partners to create sustainable programs that engage employees and demonstrate a commitment to global progress.",
  features: ["Customized corporate social responsibility programs", "Employee engagement and team-building events", "Transparent impact reporting for stakeholders", "Brand alignment with high-impact humanitarian work"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687075/img/helping_hands_corporate_partnership.png",
  badge: "Partnership"
}, {
  id: "legacy",
  label: "Legacy Giving",
  icon: Sparkles,
  title: "Ensure a Future of Hope",
  description: "Create a lasting impact that transcends generations. Legacy giving allows you to integrate your philanthropic goals into your long-term financial and estate planning.",
  features: ["Bequests and charitable gift annuities", "Endowment funds for long-term sustainability", "Recognition in our Visionary Circle society", "Expert guidance on tax-efficient giving strategies"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687075/img/helping_hands_legacy_planning.png",
  badge: "Sustainability"
}];
export default function WaysToGive() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="222953" id="ways-to-give" className="bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[1000px] h-[500px] bg-primary/5 blur-[120px] rounded-lg pointer-events-none opacity-50" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] bg-secondary/10 blur-[100px] rounded-lg pointer-events-none opacity-30" />

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
            <Sparkles className="size-4" />
            <span>Support Our Mission</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold font-default tracking-tight mb-6 text-foreground">
            Different <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Ways to Give</span>
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Helping Hands Foundation offers diverse pathways for you to contribute. Every action, whether through time, partnership, or legacy, builds a stronger foundation for those in need.
          </p>
        </div>

        {}
        <div ref={contentRef} className={`transition-all duration-1000 delay-200 ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Tabs defaultValue="volunteering" className="w-full flex flex-col items-center">
            <TabsList className="mb-12 bg-muted/50 backdrop-blur-md border border-border/50 p-1 h-auto flex-wrap justify-center gap-1 rounded-lg">
              {tabsData.map((tab, i) => <TabsTrigger key={tab.id} value={tab.id} data-index={i} className="px-6 py-3 rounded-md text-sm md:text-base data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all duration-300 font-default">
                  <tab.icon className="size-4 mr-2" />
                  {tab.label}
                </TabsTrigger>)}
            </TabsList>

            {tabsData.map((tab, i) => <TabsContent key={tab.id} value={tab.id} data-index={i} className="w-full mt-0 focus-visible:outline-none focus-visible:ring-0">
                <Card className="bg-card/40 backdrop-blur-xl border-border/50 shadow-lg shadow-primary/5 overflow-hidden rounded-lg">
                  <CardContent className="p-0">
                    <div className="grid lg:grid-cols-2 gap-0">
                      {}
                      <div className="p-8 lg:p-12 flex flex-col justify-center order-2 lg:order-1">
                        <div className="inline-block px-3 py-1 rounded-md bg-primary/10 text-primary text-xs font-bold tracking-wider uppercase mb-6 w-fit">
                          {tab.badge}
                        </div>
                        <h3 className="text-3xl font-bold font-default mb-4 text-foreground">
                          {tab.title}
                        </h3>
                        <p className="text-muted-foreground text-lg mb-8 leading-relaxed font-default">
                          {tab.description}
                        </p>
                        <ul className="space-y-4 mb-10">
                          {tab.features.map((feature, idx) => <li key={idx} data-index={idx} className="flex items-start gap-3">
                              <CircleCheck className="size-6 text-primary shrink-0 mt-0.5" />
                              <span className="text-foreground/90 font-default">{feature}</span>
                            </li>)}
                        </ul>
                        <div>
                          <Button variant="default" className="group rounded-md bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-lg font-default shadow-md transition-all hover:scale-105">
                            Inquire Now
                            <ArrowRight className="size-4 ml-2 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </div>
                      </div>

                      {}
                      <div className="relative bg-muted/30 p-8 lg:p-12 flex items-center justify-center order-1 lg:order-2 border-b lg:border-b-0 lg:border-l border-border/50 min-h-[400px]">
                        {}
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
                        
                        <div className="relative w-full max-w-md aspect-[4/3] rounded-lg overflow-hidden border border-border/50 shadow-xl shadow-black/10 group">
                           {}
                          <div className="h-8 bg-background/80 backdrop-blur-sm border-b border-border/50 flex items-center px-3 gap-1.5">
                            <div className="size-2.5 rounded-lg bg-primary/40" />
                            <div className="size-2.5 rounded-lg bg-secondary/40" />
                            <div className="size-2.5 rounded-lg bg-accent/40" />
                          </div>
                          {}
                          <img src={tab.image} alt={`${tab.label} impact preview`} className="w-full h-[calc(100%-2rem)] object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}